import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
} from "@mui/material";
import { ScheduledMeeting } from "types/meetings";
import { updateScheduledMeeting } from "api/meet/meetAPI";

interface EditMeetingModalProps {
  open: boolean;
  meeting: ScheduledMeeting | null;
  onClose: () => void;
  onMeetingUpdated: () => void;
}

const EditMeetingModal: React.FC<EditMeetingModalProps> = ({
  open,
  meeting,
  onClose,
  onMeetingUpdated,
}) => {
  const [meeting_name, setmeeting_name] = useState<string>("");
  const [meetingDate, setMeetingDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [duration, setDuration] = useState<number>(60);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [recurringFrequency, setRecurringFrequency] = useState<
    "daily" | "weekly" | "monthly" | "yearly"
  >("daily");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (meeting && open) {
      setmeeting_name(meeting.meeting_name);

      const datetime = new Date(meeting.meeting_start_datetime);
      const dateStr = datetime.toISOString().split("T")[0];
      const timeStr = datetime.toTimeString().slice(0, 5);

      setMeetingDate(dateStr);
      setStartTime(timeStr);
      setDuration(meeting.meeting_duration);

      // Set recurrence state
      if (meeting.meeting_recurrence !== "none") {
        setIsRecurring(true);
        setRecurringFrequency(
          meeting.meeting_recurrence as
            | "daily"
            | "weekly"
            | "monthly"
            | "yearly"
        );
      } else {
        setIsRecurring(false);
      }

      setError("");
    }
  }, [meeting, open]);

  const handleSubmit = async () => {
    if (!meeting) return;

    setLoading(true);
    setError("");

    try {
      const combinedDateTime = new Date(`${meetingDate}T${startTime}:00`);

      // If the recurring switch is off, pass 'none' as the recurrence value.
      const recurrenceParam = isRecurring ? recurringFrequency : "none";

      const updateData = {
        meeting_name,
        meeting_start_datetime: combinedDateTime.toISOString(),
        meeting_duration: duration,
        meeting_recurrence: recurrenceParam,
        notes: meeting.notes || "",
      };

      await updateScheduledMeeting(meeting.id, updateData);
      onMeetingUpdated();
      handleClose();
    } catch (error: any) {
      console.error("[EditMeetingModal] Failed to update meeting:", error);
      setError(error.message || "Failed to update meeting. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError("");
      onClose();
    }
  };

  const isFormValid = () => {
    return (
      meeting_name.trim() !== "" &&
      meetingDate !== "" &&
      startTime !== "" &&
      duration > 0
    );
  };

  if (!meeting) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>Edit Meeting</DialogTitle>
      <DialogContent sx={{ minHeight: "300px" }}>
        <Grid container direction="column" spacing={2}>
          {error && (
            <Grid item>
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            </Grid>
          )}

          {/* Meeting Name Input */}
          <Grid item>
            <TextField
              label="Meeting Name"
              variant="outlined"
              fullWidth
              value={meeting_name}
              onChange={(e) => setmeeting_name(e.target.value)}
              placeholder="Enter a meeting name"
              disabled={loading}
            />
          </Grid>

          {/* Scheduled Meeting Fields */}
          <Grid item>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Meeting Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={meetingDate}
                      onChange={(e) => setMeetingDate(e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Start Time"
                      type="time"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel id="duration-select-label">
                        Duration
                      </InputLabel>
                      <Select
                        labelId="duration-select-label"
                        label="Duration"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value as number)}
                        disabled={loading}
                      >
                        <MenuItem value={15}>15 min</MenuItem>
                        <MenuItem value={30}>30 min</MenuItem>
                        <MenuItem value={45}>45 min</MenuItem>
                        <MenuItem value={60}>1 hour</MenuItem>
                        <MenuItem value={120}>2 hours</MenuItem>
                        <MenuItem value={180}>3 hours</MenuItem>
                        <MenuItem value={240}>4 hours</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isRecurring}
                      onChange={(e) => setIsRecurring(e.target.checked)}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="Recurring"
                />
              </Grid>
              {isRecurring && (
                <Grid item>
                  <FormControl fullWidth>
                    <InputLabel id="recurrence-select-label">
                      Recurrence
                    </InputLabel>
                    <Select
                      labelId="recurrence-select-label"
                      label="Recurrence"
                      value={recurringFrequency}
                      onChange={(e) =>
                        setRecurringFrequency(
                          e.target.value as
                            | "daily"
                            | "weekly"
                            | "monthly"
                            | "yearly"
                        )
                      }
                      disabled={loading}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isFormValid() || loading}
        >
          {loading ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMeetingModal;
